import React, { useEffect, useMemo, useState } from 'react'
import _ from "lodash";
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useGlobalContext } from '../../../GlobalContext'
import usePrevious from '../../../../customHooks/usePrevious'
import useDispatch from '../../../../customHooks/useDispatch'
import useDimension from '../../../../customHooks/useDimension'

import { updateCompanyUser, addCompanyUser, resetCompanyError } from '../../../../actions/company'
import { createLunchCheckout, checkLunchPaymentByEmail } from '../../../../actions/payment'
import InputField from '../../../partials/form_fields/InputField'
import SelectItems from '../../../partials/form_fields/SelectItems'
import Switch from "react-switch";

import styles from './contributors.module.scss';

const CONTRIBUTOR_DRAFT_PREFIX = 'scarmor_contributor_draft';

function normalizeDateKey(value) {
  if (value === null || value === undefined || value === '' || value === 'cocktail') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toISOString().slice(0, 10);
}

function normalizeDateList(values) {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const key = normalizeDateKey(value);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(key);
  });
  return result;
}

function datesMatch(a, b) {
  return normalizeDateKey(a) === normalizeDateKey(b);
}

function listIncludesDate(list, date) {
  const key = normalizeDateKey(date);
  return !!key && normalizeDateList(list).includes(key);
}

function getContributorDraftKey(companyId) {
  const exhibitionId = localStorage.getItem('exhibition');
  return `${CONTRIBUTOR_DRAFT_PREFIX}_${companyId}_${exhibitionId}`;
}

export default function FormContributors({ user, isLoading, cancel, lunchPaymentReadyEmail, lunchPaymentPaidDays }) {
  const emailReg = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,7}$/i;
  const [formData, setFormData] = useState({});
  const [selectedPresenceDays, setSelectedPresenceDays] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [isEmailUsedByContributor, setIsEmailUsedByContributor] = useState(false);
  const [presenceDays, setPresenceDays] = useState();
  const [isLunchChecked, setIsLunchChecked] = useState(false);
  const [lunchDays, setLunchDays] = useState([]);
  const [lunchPaidDays, setLunchPaidDays] = useState([]);
  const [lunchPaid, setLunchPaid] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);


  const [context, dispatch] = useGlobalContext();
  const companyReducer = context.companyReducer

  const lunchConfig = companyReducer.companyList?.lunch || {};
  const isPaidLunchExhibition = !!(lunchConfig.isPaid && lunchConfig.price > 0);
  const isLunchSelectionAllowed = !isPaidLunchExhibition || lunchConfig.paymentEnabled !== false;
  const isLunchPaymentRequired = isPaidLunchExhibition && isLunchSelectionAllowed;
  const lunchPrice = lunchConfig.price;
  const isLunchToggleDisabled = isPaidLunchExhibition && !isLunchSelectionAllowed;
  const isMultiDaySalon = (presenceDays || []).length > 1;

  const unpaidLunchDays = useMemo(() => {
    return normalizeDateList(lunchDays).filter((day) => !listIncludesDate(lunchPaidDays, day));
  }, [lunchDays, lunchPaidDays]);

  const allSelectedLunchPaid = lunchDays.length > 0 && unpaidLunchDays.length === 0;
  const needsPayment = isLunchPaymentRequired
    && (isMultiDaySalon ? lunchDays.length > 0 : isLunchChecked)
    && (isMultiDaySalon ? unpaidLunchDays.length > 0 : !lunchPaid);
  const canSubmit = isValid && !needsPayment;
  const paymentTotal = (lunchPrice || 0) * (isMultiDaySalon ? unpaidLunchDays.length : 1);

  const wasLoading = usePrevious(isLoading)
  const dimension = useDimension()

  const [isEmailError, setIsEmailError] = useState(false)

  //ACTIONS
  const _updateCompanyUser = (data, id, actionLoad) => updateCompanyUser(dispatch, data, id, actionLoad)
  const _addCompanyUser = (data) => addCompanyUser(dispatch, data)
  const _resetCompanyError = useDispatch(resetCompanyError, dispatch)

  function saveFormDraft() {
    if (user || !companyReducer.company?._id) return;
    const draft = {
      formData,
      selectedPresenceDays,
      isLunchChecked,
      lunchDays,
      lunchPaidDays,
      lunchPaid,
    };
    sessionStorage.setItem(
      getContributorDraftKey(companyReducer.company._id),
      JSON.stringify(draft)
    );
  }

  function restoreFormDraft() {
    if (user || !companyReducer.company?._id) return false;
    const raw = sessionStorage.getItem(getContributorDraftKey(companyReducer.company._id));
    if (!raw) return false;

    try {
      const draft = JSON.parse(raw);
      if (draft.formData) setFormData(draft.formData);
      if (draft.selectedPresenceDays) setSelectedPresenceDays(draft.selectedPresenceDays);
      if (draft.isLunchChecked !== undefined) setIsLunchChecked(draft.isLunchChecked);
      if (draft.lunchDays) setLunchDays(normalizeDateList(draft.lunchDays));
      if (draft.lunchPaidDays) setLunchPaidDays(normalizeDateList(draft.lunchPaidDays));
      if (draft.lunchPaid !== undefined) setLunchPaid(draft.lunchPaid);
      return true;
    } catch (error) {
      return false;
    }
  }

  function clearFormDraft() {
    if (!companyReducer.company?._id) return;
    sessionStorage.removeItem(getContributorDraftKey(companyReducer.company._id));
  }

  function handleCancel() {
    if (!user) clearFormDraft();
    cancel();
  }

  // HANDLE ERROR ON EMAIL
  useEffect(() => {
    setIsEmailError(/email/.test(companyReducer.error))
  }, [companyReducer.error])

  useEffect(() => {
    let days = companyReducer.companyList.days.filter(d => d.value !== "cocktail")
    const getDateStr = (date) => {
      const day = format(new Date(date), 'd MMMM', { locale: fr });
      return day;
    }
    days = days.map((day) => ({ value: day.value, label: getDateStr(day.value) } ));
    setPresenceDays([
      ...days,
    ])
  }, []);

  // RESET COMPANY ERROR WHEN UNMOUNT
  useEffect(() => {
    return () => {
      _resetCompanyError()
    }
    // eslint-disable-next-line
  }, [])

  // IF NO USER AND ONLY ON PRESENCE DAYS
  // SELECT IT BY DEFAULT
  useEffect(() => {
    if (!user && presenceDays?.length === 1) {
      const hasDraft = companyReducer.company?._id &&
        sessionStorage.getItem(getContributorDraftKey(companyReducer.company._id));
      if (hasDraft) return;

      setFormData({
        presence: [presenceDays[0].value],
      })
      setSelectedPresenceDays([presenceDays[0].value]);
    }
  }, [presenceDays, user, companyReducer.company?._id]);

  // Restore draft after Stripe redirect (create flow only)
  useEffect(() => {
    if (user || !companyReducer.company?._id) return;
    restoreFormDraft();
    if (lunchPaymentReadyEmail && isLunchSelectionAllowed) {
      const paid = normalizeDateList(lunchPaymentPaidDays);
      if (paid.length > 0) {
        setLunchPaidDays((prev) => normalizeDateList([...prev, ...paid]));
        setLunchDays((prev) => normalizeDateList([...prev, ...paid]));
        setIsLunchChecked(true);
        setLunchPaid(true);
        setFormData((prev) => ({ ...prev, lunch: true, lunchDays: normalizeDateList([...(prev.lunchDays || []), ...paid]) }));
      } else {
        setLunchPaid(true);
        setIsLunchChecked(true);
        setFormData((prev) => ({ ...prev, lunch: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, companyReducer.company?._id, lunchPaymentReadyEmail, isLunchSelectionAllowed, lunchPaymentPaidDays]);

  // INIT PRESENCE DAYS
  // IF THE ARE AN formData
  useEffect(() => {
    if (!user) return;

    let obj = {
      _id: user._id,
      email: user.email,
      profile: {
        lastName: user.profile.lastName,
        firstName: user.profile.firstName,
        function: user.profile.function,
        phone: user.profile.phone
      },
      presence: user.presence
    }
    if (user.presence) {
      obj.presence = user.presence
      setSelectedPresenceDays(user.presence)
    }

    const userLunchDays = normalizeDateList(
      user.lunchDays?.length
        ? user.lunchDays
        : (user.lunch ? user.presence : [])
    );
    const userPaidDays = normalizeDateList(
      user.lunchPaidDays?.length
        ? user.lunchPaidDays
        : (user.lunchPaid ? userLunchDays : [])
    );

    setLunchDays(userLunchDays);
    setLunchPaidDays(userPaidDays);
    setIsLunchChecked(user.lunch || userLunchDays.length > 0);
    setLunchPaid(!!user.lunchPaid || (userLunchDays.length > 0 && userLunchDays.every((d) => listIncludesDate(userPaidDays, d))));
    obj.lunch = user.lunch || userLunchDays.length > 0;
    obj.lunchDays = userLunchDays;
    setFormData(obj)
  }, [user])

  useEffect(() => {
    if (user?.lunchPaid) {
      setLunchPaid(true);
    }
    if (user?.lunchPaidDays?.length) {
      setLunchPaidDays(normalizeDateList(user.lunchPaidDays));
    }
  }, [user?.lunchPaid, user?.lunchPaidDays]);

  // CLOSE FORM CONTACTS AFTER SUBMIT
  useEffect(() => {
    if (wasLoading && !isLoading) {
      cancel()
    }
  }, [isLoading])

  // HANDLE IS READY TO SUBMIT
  useEffect(() => {
    if (formData &&
      (formData.profile &&
        (formData.profile.lastName && formData.profile.lastName !== "") &&
        (formData.profile.firstName && formData.profile.firstName !== "") &&
        (formData.profile.function && formData.profile.function !== "") &&
        (formData.presence && formData.presence.length > 0)
      ) && (
        formData.email &&
        emailReg.test(formData.email)
      ) &&
      !isEmailUsedByContributor
    ) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }

  }, [formData, isEmailUsedByContributor])

  useEffect(() => {
    if (lunchPaymentReadyEmail && formData.email === lunchPaymentReadyEmail && isLunchSelectionAllowed) {
      const paid = normalizeDateList(lunchPaymentPaidDays);
      if (paid.length > 0) {
        setLunchPaidDays((prev) => normalizeDateList([...prev, ...paid]));
        setLunchDays((prev) => normalizeDateList([...prev, ...paid]));
      }
      setLunchPaid(true);
      setIsLunchChecked(true);
      setFormData((prev) => ({ ...prev, lunch: true }));
    }
  }, [lunchPaymentReadyEmail, formData.email, isLunchSelectionAllowed, lunchPaymentPaidDays]);

  useEffect(() => {
    let cancelled = false;

    async function checkEmailLunchPayment() {
      const wantsLunch = isMultiDaySalon ? lunchDays.length > 0 : isLunchChecked;
      if (
        !isLunchPaymentRequired
        || !wantsLunch
        || !formData.email
        || !emailReg.test(formData.email)
      ) {
        return;
      }

      if (lunchPaymentReadyEmail && formData.email === lunchPaymentReadyEmail) {
        setLunchPaid(true);
        return;
      }

      const res = await checkLunchPaymentByEmail(
        dispatch,
        formData.email,
        formData._id || user?._id,
        isMultiDaySalon ? lunchDays : undefined
      );
      if (!cancelled) {
        const paidDays = normalizeDateList(res?.lunchPaidDays);
        if (paidDays.length) {
          setLunchPaidDays(paidDays);
        }
        if (isMultiDaySalon) {
          setLunchPaid(lunchDays.length > 0 && lunchDays.every((d) => listIncludesDate(paidDays, d) || listIncludesDate(res?.lunchPaidDays, d)));
        } else {
          setLunchPaid(!!res?.lunchPaid);
        }
      }
    }

    checkEmailLunchPayment();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    formData.email,
    formData._id,
    user?._id,
    isLunchChecked,
    lunchDays,
    isMultiDaySalon,
    isLunchPaymentRequired,
    lunchPaymentReadyEmail,
  ]);

  function resolveContributorFromStore({ email, userId } = {}) {
    if (!companyReducer.companyUsers?.length) return null;
    if (userId) {
      return companyReducer.companyUsers.find((u) => u._id === userId) || null;
    }
    if (email) {
      return companyReducer.companyUsers.find((u) => u.email === email) || null;
    }
    return null;
  }

  function syncLunchStateFromContributor(contributor) {
    if (!contributor) return;
    const contributorLunchDays = normalizeDateList(
      contributor.lunchDays?.length
        ? contributor.lunchDays
        : (contributor.lunch ? contributor.presence : [])
    );
    const contributorPaidDays = normalizeDateList(
      contributor.lunchPaidDays?.length
        ? contributor.lunchPaidDays
        : (contributor.lunchPaid ? contributorLunchDays : [])
    );

    if (contributorPaidDays.length) {
      setLunchPaidDays(contributorPaidDays);
    }
    if (contributor.lunchPaid || (contributorLunchDays.length > 0 && contributorLunchDays.every((d) => listIncludesDate(contributorPaidDays, d)))) {
      setLunchPaid(true);
    }
    if ((contributor.lunch || contributorLunchDays.length > 0) && isLunchSelectionAllowed) {
      setIsLunchChecked(true);
      setLunchDays(contributorLunchDays);
      setFormData((prev) => ({ ...prev, lunch: true, lunchDays: contributorLunchDays }));
    }
  }

  useEffect(() => {
    const contributor = resolveContributorFromStore({
      userId: user?._id || formData._id,
      email: formData.email,
    });
    syncLunchStateFromContributor(contributor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyReducer.companyUsers, user, formData._id, formData.email, isLunchSelectionAllowed]);

  useEffect(() => {
    if (!isLunchToggleDisabled || user) return;
    setIsLunchChecked(false);
    setLunchPaid(false);
    setLunchDays([]);
    setLunchPaidDays([]);
    setFormData((prev) => ({ ...prev, lunch: false, lunchDays: [] }));
  }, [isLunchToggleDisabled, user]);

  // Keep lunchDays within selected presence days
  useEffect(() => {
    if (!isMultiDaySalon) return;
    const presenceKeys = normalizeDateList(selectedPresenceDays);
    setLunchDays((prev) => {
      const filtered = prev.filter((day) => presenceKeys.includes(day));
      if (filtered.length === prev.length) return prev;
      setFormData((formPrev) => ({ ...formPrev, lunchDays: filtered, lunch: filtered.length > 0 }));
      setIsLunchChecked(filtered.length > 0);
      return filtered;
    });
  }, [selectedPresenceDays, isMultiDaySalon]);

  function handleChange(val, error, field) {

    let updatedData = { ...formData }

    if (field === "email" && !error) {
      const matchedContributor = companyReducer.companyUsers?.find((u) => u.email === val);

      let found = companyReducer.companyUsers.find((user) => {
        return user.email === val && user.presence.length === 0
      })

      // USER IS A PROPS PASS TO THIS COMPOMENT TO
      // EDIT A USER
      let foundContributors;
      if (!user) {
        foundContributors = companyReducer.companyUsers.find((user) => {
          return user.email === val && user.presence.length > 0
        })
        setIsEmailUsedByContributor(!!foundContributors)
      }

      if (found && !foundContributors) {
        _.set(updatedData, "_id", found._id)
        _.set(updatedData, "profile.lastName", found.profile.lastName)
        _.set(updatedData, "profile.firstName", found.profile.firstName)
        _.set(updatedData, "profile.function", found.profile.function)
        syncLunchStateFromContributor(found);

      } else if (matchedContributor) {
        syncLunchStateFromContributor(matchedContributor);
      } else {
        _.unset(updatedData, "_id")
        if (!lunchPaymentReadyEmail || lunchPaymentReadyEmail !== val) {
          if (!matchedContributor?.lunchPaid) {
            setLunchPaid(false);
          }
        }
      }
    } else if (field === "email") {
      setIsEmailUsedByContributor(false)
    }
    if (field === "presence" && val) {
      let found = presenceDays.filter(day => val.includes(day.label));
      let updatedDays = found.map((d) => d.value);
      setSelectedPresenceDays(updatedDays);
      _.set(updatedData, field, updatedDays);
    } else {
      _.set(updatedData, field, val);
    }

    setFormData(updatedData)
  }

  function submit() {
    if (!canSubmit || companyReducer.isLoading || isEmailUsedByContributor) return;
    if (formData._id) {
      submitUdpateformData()
    } else {
      submitCreateformData()
    }
  }

  function buildLunchPayload(data) {
    if (isMultiDaySalon) {
      data.lunchDays = normalizeDateList(lunchDays);
      data.lunch = data.lunchDays.length > 0;
    } else {
      data.lunch = isLunchToggleDisabled ? !!(user?.lunch ?? formData.lunch) : isLunchChecked;
      if (data.lunch && selectedPresenceDays.length === 1) {
        data.lunchDays = [normalizeDateKey(selectedPresenceDays[0]) || selectedPresenceDays[0]];
      } else if (!data.lunch) {
        data.lunchDays = [];
      }
    }
    return data;
  }

  function submitUdpateformData() {
    var data = _.cloneDeep(formData)
    data = buildLunchPayload(data)

    if (!data.presence) {
      data.presence = []
    }

    data.presence = data.presence.filter(d => !!d);
    _updateCompanyUser(data, formData._id, !user ? "update-contributors" : null)

  }

  function submitCreateformData() {
    let data = _.cloneDeep(formData)
    if (isLunchToggleDisabled) {
      data.lunch = false;
      data.lunchDays = [];
    } else {
      data = buildLunchPayload(data);
    }
    data.company = companyReducer.company._id

    if (!data.presence) {
      data.presence = []
    }

    data.presence = data.presence.filter(d => !!d)
    clearFormDraft();
    _addCompanyUser(data)

  }

  function toggleLunch(val) {
    if (isLunchToggleDisabled) return;
    let updatedData = { ...formData }
    _.set(updatedData, "lunch", val)
    if (val && selectedPresenceDays.length === 1) {
      const day = normalizeDateKey(selectedPresenceDays[0]) || selectedPresenceDays[0];
      setLunchDays([day]);
      _.set(updatedData, "lunchDays", [day]);
    } else if (!val) {
      setLunchDays([]);
      _.set(updatedData, "lunchDays", []);
    }
    setFormData(updatedData)
    setIsLunchChecked(val)
    if (!val) {
      setLunchPaid(false)
    } else if (user?.lunchPaid || allSelectedLunchPaid) {
      setLunchPaid(true)
    } else if (lunchPaymentReadyEmail && formData.email === lunchPaymentReadyEmail) {
      setLunchPaid(true)
    }
  }

  function handleLunchDaysChange(labels) {
    if (isLunchToggleDisabled) return;
    const options = getPresenceDayOptionsForLunch();
    const found = options.filter((day) => (labels || []).includes(day.label));
    const nextDays = found.map((day) => normalizeDateKey(day.value) || day.value);
    setLunchDays(nextDays);
    setIsLunchChecked(nextDays.length > 0);
    setFormData((prev) => ({
      ...prev,
      lunch: nextDays.length > 0,
      lunchDays: nextDays,
    }));
    const unpaid = nextDays.filter((d) => !listIncludesDate(lunchPaidDays, d));
    setLunchPaid(nextDays.length > 0 && unpaid.length === 0);
  }

  async function handleLunchPayment() {
    if (!emailReg.test(formData.email) || !companyReducer.company?._id || isPaymentLoading) {
      return;
    }

    const datesToPay = isMultiDaySalon
      ? unpaidLunchDays
      : (selectedPresenceDays.length
        ? [normalizeDateKey(selectedPresenceDays[0]) || selectedPresenceDays[0]]
        : undefined);

    setIsPaymentLoading(true);
    try {
      const response = await createLunchCheckout(dispatch, {
        email: formData.email,
        companyId: companyReducer.company._id,
        userId: formData._id || user?._id || undefined,
        dates: datesToPay
      });

      if (response?.url) {
        saveFormDraft();
        window.location.href = response.url;
      }
    } finally {
      setIsPaymentLoading(false);
    }
  }

  function getSelectedItems() {
    let found = presenceDays.filter(day => selectedPresenceDays.includes(day.value));
    return (found && found.length > 0) ? found.map(d => d.label) : []
  }

  function getPresenceDayOptionsForLunch() {
    return (presenceDays || []).filter((day) =>
      selectedPresenceDays.some((selected) => datesMatch(selected, day.value))
    );
  }

  function renderLunchSection() {
    if (isMultiDaySalon) {
      const lunchDayOptions = getPresenceDayOptionsForLunch();
      const selectedLunchLabels = lunchDayOptions
        .filter((day) => listIncludesDate(lunchDays, day.value))
        .map((day) => day.label);
      const paidLunchLabels = lunchDayOptions
        .filter((day) => listIncludesDate(lunchDays, day.value) && listIncludesDate(lunchPaidDays, day.value))
        .map((day) => day.label);

      return (
        <div className={`${styles['container-input']} ${styles['container-select-items']} container-input`}>
          <label>
            Présence au déjeuner
            <span className={`${styles.required} required`}>*</span>
          </label>
          {isLunchToggleDisabled &&
            <p className="warn" style={{ marginTop: 8, marginBottom: 10 }}>
              L'inscription au repas n'est plus possible
            </p>
          }
          {selectedPresenceDays.length === 0 &&
            <p className="warn" style={{ marginTop: 8, marginBottom: 10 }}>
              Sélectionnez d'abord vos jours de présence.
            </p>
          }
          {lunchDayOptions.length > 0 &&
            <div style={isLunchToggleDisabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
              <SelectItems
                items={lunchDayOptions.map((day) => day.label)}
                selectedItems={selectedLunchLabels}
                uniq={false}
                handleChange={(val) => handleLunchDaysChange(val)}
              />
            </div>
          }
          {paidLunchLabels.length > 0 &&
            <p className="text-primary" style={{ marginBottom: 10, color: 'rgb(0, 136, 0)' }}>
              Payé : {paidLunchLabels.join(', ')}
            </p>
          }
        </div>
      );
    }

    return (
      <div className="container-switch">
        <p>
          Présence au déjeuner
          <span className="required">*</span>
        </p>
        {isLunchToggleDisabled &&
          <p className="warn" style={{ marginTop: 8, marginBottom: 10 }}>
            L'inscription au repas n'est plus possible
          </p>
        }
        <Switch
          onColor='#080'
          offColor='#FF0009'
          onChange={toggleLunch}
          checked={isLunchChecked}
          disabled={isLunchToggleDisabled}
        />
      </div>
    );
  }

  return (
    <MobileWrapper isMobile={dimension.width < 767}>
      <section className={dimension.width > 767 ? styles['module-contact'] : `${styles['module-contact']} ${styles.mobile}`}>

        {dimension.width < 767 ?
          <h2>
            Inscription d’un collaborateur
          </h2> : null
        }
        <div className={styles['grid-form']}>
          <div className={styles['container-email']}>
            <InputField
              id={"email"}
              value={formData.email ? formData.email : ""}
              title={"Email"}
              placeholder={""}
              type={"string"}
              format={"email"}
              required={true}
              disabled={false}
              handleChange={(val, error) => handleChange(val, error, "email")}
            />
            {isEmailError && companyReducer.error ?
              <p className="warn">{companyReducer.error}</p> : null
            }
            {isEmailUsedByContributor &&
              <p className="warn">Un collaborateur utilise déjà cette email</p>
            }
          </div>
          <InputField
            id={"lastName"}
            value={formData.profile ? formData.profile.lastName : ""}
            title={"Nom"}
            placeholder={""}
            type={"string"}
            textTransform={"uppercase"}
            required={true}
            disabled={
              !emailReg.test(formData.email) ||
              isEmailUsedByContributor
            }
            handleChange={(val, error) => handleChange(val, error, "profile.lastName")}
          />
          <InputField
            id={"firstName"}
            value={formData.profile ? formData.profile.firstName : ""}
            title={"Prénom"}
            placeholder={""}
            type={"string"}
            textTransform={"capitalized"}
            required={true}
            disabled={
              !emailReg.test(formData.email) ||
              isEmailUsedByContributor
            }
            handleChange={(val, error) => handleChange(val, error, "profile.firstName")}
          />
          <InputField
            id={"function"}
            value={formData.profile ? formData.profile.function : ""}
            title={"Fonction"}
            placeholder={""}
            type={"string"}
            textTransform={"uppercase"}
            required={true}
            disabled={
              !emailReg.test(formData.email) ||
              isEmailUsedByContributor
            }
            maxLength={"30"}
            handleChange={(val, error) => handleChange(val, error, "profile.function")}
          />
          <div className={styles['container-phone']}>
            <InputField
              value={formData.profile ? formData.profile.phone : ""}
              title={"Téléphone"}
              placeholder={""}
              type={"string"}
              maskNumber={"## ## ## ## ##"}
              format={"phone"}
              handleChange={(val, error) => handleChange(val, error, "profile.phone")}
            />
          </div>
        </div>
        <div className={`${styles['container-input']} ${styles['container-select-items']} container-input`}>
          <label>Présence sur le salon <span className={`${styles.required} required`}>*</span></label>
          {(presenceDays && presenceDays.length > 0) &&
            <SelectItems
              items={presenceDays.map(day => day.label)}
              selectedItems={getSelectedItems()}
              required={true}
              uniq={false}
              handleChange={(val, error) => handleChange(val, error, "presence")}
            />
          }
        </div>
        {renderLunchSection()}
        {needsPayment &&
          <div className={styles['container-button']} style={{ marginBottom: 10, padding: 0, justifyContent: 'flex-start' }}>
            <button onClick={handleLunchPayment} disabled={!isValid || isPaymentLoading} style={{margin: 0, marginTop: 10}}>
              {isMultiDaySalon
                ? `Payer ${unpaidLunchDays.length} déjeuner${unpaidLunchDays.length > 1 ? 's' : ''} (${paymentTotal} €)`
                : `Payer le déjeuner (${lunchPrice} €)`
              }
              {isPaymentLoading ?
                <div className={`${styles.loader} loader`}></div> : null
              }
            </button>
          </div>
        }
        {!isMultiDaySalon && lunchPaid && isLunchChecked &&
          <p className="text-primary" style={{ marginBottom: 10, color: 'rgb(0, 136, 0)' }}> - Déjeuner payé</p>
        }
        {isMultiDaySalon && lunchDays.length > 0 && unpaidLunchDays.length === 0 &&
          <p className="text-primary" style={{ marginBottom: 10, color: 'rgb(0, 136, 0)' }}> - Déjeuners payés</p>
        }
        <div className={styles['container-button']}>
          <button className={`${styles.secondary} secondary`} onClick={handleCancel}>Annuler</button>
          <button className={!canSubmit ? "disabled" : ""} onClick={submit}>
            {formData && formData._id ?
              "Modifier" : "Ajouter"
            }
            {companyReducer.isLoading ?
              <div className={`${styles.loader} loader`}></div> : null
            }
          </button>
        </div>
        {!isEmailError && companyReducer.error ?
          <p className="warn">{companyReducer.error}</p> : null
        }
      </section>
    </MobileWrapper>
  )
}

const MobileWrapper = ({ isMobile, wrapper, children }) => {
  return (
    isMobile ?
      <div className="container-mobile">
        <div className="overlay-module-contact"></div>
        {children}
      </div> : children
  )
}
